import { Button } from "@/components/ui/button";
import { CalendarPlus, Download, Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

// Generate ICS file content
const generateICS = (sagra) => {
  const formatDate = (dateStr, time = '00:00') => {
    const date = new Date(dateStr);
    const [hours, minutes] = time.split(':');
    date.setHours(parseInt(hours) || 0, parseInt(minutes) || 0, 0);
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };

  const startDate = formatDate(sagra.data_inizio, sagra.orario_apertura || '10:00');
  const endDate = formatDate(sagra.data_fine, sagra.orario_chiusura || '23:00');
  const now = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  
  const location = `${sagra.indirizzo}, ${sagra.comune}, ${sagra.provincia}, Italia`;
  const description = `${sagra.descrizione}\n\nSpecialità: ${sagra.specialita?.join(', ') || 'N/A'}\n\nOrari: ${sagra.orario_apertura || 'N/A'} - ${sagra.orario_chiusura || 'N/A'}\n\nContatti: ${sagra.contatti?.nome_organizzatore || 'N/A'} - ${sagra.contatti?.telefono || 'N/A'}`;

  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Sagre Italia//NONSGML v1.0//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${sagra.id}@sagreitalia.it
DTSTAMP:${now}
DTSTART:${startDate}
DTEND:${endDate}
SUMMARY:${sagra.nome}
DESCRIPTION:${description.replace(/\n/g, '\\n')}
LOCATION:${location}
${sagra.coordinate ? `GEO:${sagra.coordinate.lat};${sagra.coordinate.lng}` : ''}
STATUS:CONFIRMED
TRANSP:OPAQUE
END:VEVENT
END:VCALENDAR`;

  return icsContent;
};

// Download ICS file
const downloadICS = (sagra) => {
  const icsContent = generateICS(sagra);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${sagra.nome.replace(/[^a-zA-Z0-9]/g, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  toast.success("Evento scaricato! Aprilo per aggiungerlo al calendario.");
};

// Generate Google Calendar URL
const getGoogleCalendarUrl = (sagra) => {
  const formatDateGoogle = (dateStr) => {
    return dateStr.replace(/-/g, '');
  };
  
  const startDate = formatDateGoogle(sagra.data_inizio);
  const endDate = formatDateGoogle(sagra.data_fine);
  const location = encodeURIComponent(`${sagra.indirizzo}, ${sagra.comune}, ${sagra.provincia}, Italia`);
  const title = encodeURIComponent(sagra.nome);
  const details = encodeURIComponent(`${sagra.descrizione}\n\nSpecialità: ${sagra.specialita?.join(', ') || 'N/A'}\n\nOrari: ${sagra.orario_apertura || 'N/A'} - ${sagra.orario_chiusura || 'N/A'}`);
  
  return `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&details=${details}&location=${location}&sf=true&output=xml`;
};

// Generate Outlook Calendar URL
const getOutlookCalendarUrl = (sagra) => {
  const startDate = sagra.data_inizio;
  const endDate = sagra.data_fine;
  const location = encodeURIComponent(`${sagra.indirizzo}, ${sagra.comune}, ${sagra.provincia}, Italia`);
  const title = encodeURIComponent(sagra.nome);
  const body = encodeURIComponent(sagra.descrizione);
  
  return `https://outlook.live.com/calendar/0/action/compose?subject=${title}&startdt=${startDate}&enddt=${endDate}&body=${body}&location=${location}`;
};

const AddToCalendar = ({ sagra }) => {
  const calendarOptions = [
    {
      name: "Google Calendar",
      icon: "🗓️",
      action: () => window.open(getGoogleCalendarUrl(sagra), '_blank')
    },
    {
      name: "Outlook",
      icon: "📅",
      action: () => window.open(getOutlookCalendarUrl(sagra), '_blank')
    },
    {
      name: "Apple Calendar / Altri",
      icon: "📆",
      action: () => downloadICS(sagra)
    }
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className="rounded-lg gap-1.5"
          data-testid="add-to-calendar-btn"
        >
          <CalendarPlus className="w-4 h-4" />
          Calendario
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="end">
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground mb-3">Aggiungi a</p>
          
          {calendarOptions.map((option) => (
            <button
              key={option.name}
              onClick={option.action}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-muted hover:bg-muted/80 text-foreground transition-colors"
              data-testid={`calendar-${option.name.toLowerCase().replace(/[^a-z]/g, '')}`}
            >
              <span className="text-lg">{option.icon}</span>
              <span className="text-sm font-medium">{option.name}</span>
            </button>
          ))}
          
          <div className="border-t border-border pt-2 mt-2">
            <button
              onClick={() => downloadICS(sagra)}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              data-testid="download-ics-btn"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm font-medium">Scarica file .ics</span>
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AddToCalendar;
